import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import loadable from '@loadable/component';
import { useTranslation } from 'react-i18next';
import './index.scss';
import Dialog from '../Dialog';
import DialogCloseButton from '../Dialog/DialogCloseButton';
import TextMaskCustom from '../../static/TextMaskCustom';
import { AuthContext } from '../../../Contexts';
import { sendAppPromoContact } from '../../../services/Api/Coupon/newCouponService';
import {
  DRUG_CLASS_CTA,
  GET_APP_MODAL_SENT_SIGNUP_MODAL,
  GTM_TEXT_APP_DOWNLOAD_LINK_SUCCESS
} from '../../../constants/defaults';
const AuthenticateModal = loadable(() =>
  import('../../HomeDeliveryNew/AuthenticateModal')
);

// Masked value "(123) 456-7890" is 14 chars when complete
const MASKED_PHONE_LENGTH = 14;

const GetAppModal = ({ closeModal, ctaLinks, qrImageSrc, gtmLocation }) => {
  const [inputValue, setInputValue] = useState('');
  const [invalidInput, setInvalidInput] = useState(false);
  const [smsConsentChecked, setSmsConsentChecked] = useState(false);
  const [smsConsentError, setSmsConsentError] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [isSignupModalOpen, setSignupModal] = useState(false);
  const closeButtonRef = useRef();
  const inputText = useRef();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isUserLogin = useContext(AuthContext)?.userData?.isLoggedIn;
  const isPhoneValid = inputValue.length === MASKED_PHONE_LENGTH;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    (inputText.current?.inputElement || inputText.current)?.focus();
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const updateInput = (event = { target: { value: '' } }) => {
    const { value } = event.target;
    setInputValue(value);
    if (value.length === MASKED_PHONE_LENGTH) setInvalidInput(false);
  };

  const validateInput = () => {
    setInvalidInput(!isPhoneValid);
    setSmsConsentError(!smsConsentChecked);
    return isPhoneValid && smsConsentChecked;
  };

  const postContactAPI = async () => {
    setApiLoading(true);
    try {
      const res = await sendAppPromoContact(inputValue.replace(/\D/g, ''));
      if (res?.data?.result) {
        setContactSent(true);
        _.triggerGTMVirtualPageViewEvent({
          event: GTM_TEXT_APP_DOWNLOAD_LINK_SUCCESS,
          formId: 'mobile-app',
          clickText: 'Send',
          location: gtmLocation
        });
      }
    } catch (err) {
      console.log('err in GetAppModal postContactAPI', err);
    } finally {
      setApiLoading(false);
    }
  };

  const handleSubmit = (event = { preventDefault() {} }) => {
    event.preventDefault();
    if (validateInput()) postContactAPI();
  };

  const handleKeydownEvent = event => {
    if (event.key === 'Enter') {
      event.stopPropagation();
      handleSubmit(event);
    }
  };

  const handleSignUpButtonClick = () => {
    setSignupModal(true);
    const { signUpPath } = _.getSignUpPath(GET_APP_MODAL_SENT_SIGNUP_MODAL);
    _.triggerGTMVirtualPageViewEvent({
      event: 'signUpButtonClick',
      ...signUpPath
    });
    _.triggerGTMVirtualPageViewEvent({
      event: 'signupModalOpen',
      ...signUpPath
    });
  };

  const setFocusToCloseButton = event => {
    if (!_.isEmpty(event.relatedTarget)) {
      if (event.relatedTarget.className.includes('dialogComponent')) return;
      closeButtonRef.current?.focus();
    }
  };

  if (isSignupModalOpen) {
    return (
      <div className="mainModal afterSignupmodal">
        <div className="transparentBg" onClick={closeModal}></div>
        <div className="signUpModal">
          <AuthenticateModal hideAuthenticateModal={closeModal} />
        </div>
      </div>
    );
  }

  const renderForm = () => (
    <div className="getAppModalBody">
      <form className="getAppModalForm" autoComplete="on" noValidate>
        <h3 className="getAppModalHeading">{t('Text yourself a link')}</h3>
        <div className="getAppModalField">
          <label htmlFor="getAppModalPhone" className="getAppModalLabel">
            {t('GetAppModal-phone-label')}
          </label>
          <div className="inputWrapper">
            <TextMaskCustom
              id="getAppModalPhone"
              type="text"
              value={inputValue}
              onChange={updateInput}
              onBlur={() => inputValue && validateInput()}
              onKeyDown={handleKeydownEvent}
              className={`themeFocus dialogComponent ${
                invalidInput ? 'invalid-input-error' : ''
              }`}
              placeholder="(123) 456-7890"
              ref={inputText}
              aria-required="true"
              autoComplete="tel-national"
              aria-invalid={invalidInput ? 'true' : 'false'}
              aria-describedby="getAppModalPhoneErr"
              inputMode="numeric"
            />
            {invalidInput && (
              <img
                className="errorImage"
                src="/content/images/icons/error.svg"
                alt=""
              />
            )}
          </div>
          {invalidInput && (
            <span
              id="getAppModalPhoneErr"
              className="getAppModalError"
              role="alert"
            >
              {t('GetAppModal-phone-error')}
            </span>
          )}
        </div>
        <div className="smsConsentWrapper">
          <div className="smsConsentCheckboxRow">
            <input
              type="checkbox"
              id="getAppModalConsent"
              className={`dialogComponent ${
                smsConsentError ? 'invalid-input-error' : ''
              }`}
              checked={smsConsentChecked}
              onChange={() => {
                setSmsConsentChecked(prev => !prev);
                setSmsConsentError(false);
              }}
              aria-required="true"
              aria-invalid={smsConsentError ? 'true' : 'false'}
              aria-describedby="getAppModalConsentErr"
            />
            <label htmlFor="getAppModalConsent" className="smsConsentLabel">
              {t('SMS-consent-checkbox-label')}
            </label>
          </div>
          <p className="smsConsentDisclaimer">
            {t('SMS-consent-disclaimer-intro')}&nbsp;
            <a
              href={_.withLocale(lang, '/terms-and-conditions')}
              target="_blank"
              rel="noopener noreferrer"
              className="dialogComponent"
            >
              {t('Terms & Conditions Text')}
            </a>
            &nbsp;{t('SMS-consent-and')}&nbsp;
            <a
              href={_.withLocale(lang, '/privacy-policy')}
              target="_blank"
              rel="noopener noreferrer"
              className="dialogComponent"
            >
              {t('Privacy Policy')}
            </a>
            &nbsp;{t('SMS-consent-disclaimer-rest')}
          </p>
          {smsConsentError && (
            <span
              id="getAppModalConsentErr"
              className="getAppModalError"
              role="alert"
            >
              {t('SMS-consent-required-error')}
            </span>
          )}
        </div>
        <div className="getAppModalAction">
          {!apiLoading ? (
            <button
              type="submit"
              className="button pink themeFocus dialogComponent getAppModalSend"
              onClick={handleSubmit}
              // aria-disabled (not disabled) so clicking it still shows
              // the phone / consent errors, as in the Figma error state
              aria-disabled={!isPhoneValid || !smsConsentChecked}
            >
              {t('Send')}
            </button>
          ) : (
            <div className="loaderDiv" role="status">
              <img
                src="/content/images/loader/loading.gif"
                alt={t('Text Sending')}
                className="sendLoader"
              />
            </div>
          )}
        </div>
      </form>
      <div className="getAppModalDivider" aria-hidden="true"></div>
      <div className="getAppModalQr">
        <h3 className="getAppModalHeading">{t('GetAppModal-scan-heading')}</h3>
        <img
          className="getAppModalQrImage"
          src={
            qrImageSrc ||
            `/content/images/apps/${ctaLinks.QR_CODE}_qr_code-new.svg`
          }
          height={210}
          width={210}
          alt={t('QR code to download the app')}
        />
      </div>
    </div>
  );

  const renderSent = () => (
    <div className="getAppModalSent">
      <div className="getAppModalSentBanner" role="status">
        {t('GetAppModal-sent-banner')}
      </div>
      {!isUserLogin && (
        <div className="getAppModalSignup">
          <div className="getAppModalSignupContent">
            <h3>
              {t('CouponModal-Sign up')} <span>{t('FREE')}</span>
              {t('CouponModal-to get')}&nbsp;
              {t('lowest prices')}
            </h3>
            <button
              onClick={handleSignUpButtonClick}
              className="pink dialogComponent"
            >
              {t('Sign me up')}
            </button>
            <p>
              *{t('Some exclusions apply. See')}{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={_.withLocale(lang, '/terms-and-conditions')}
                className="dialogComponent"
              >
                {t('Terms and Conditions.')}
              </a>
            </p>
          </div>
          <img
            className="getAppModalSignupImage"
            src="/content/images/bg/signupLadyNew.png"
            alt=""
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="mainModal getAppModal">
      <div className="transparentBg" onClick={closeModal}></div>
      <Dialog
        className="getAppModalContent"
        onBlur={setFocusToCloseButton}
        escKeyFunction={closeModal}
        isModalOpen
      >
        <div className="getAppModalHeader">
          <h2 id="dialogTitleHeading">{t('GetAppModal-title')}</h2>
          <DialogCloseButton
            ref={closeButtonRef}
            className="closeModal whiteFocus"
            onClick={closeModal}
            img="icon-close-white.svg"
            alt=""
          />
        </div>
        {contactSent ? renderSent() : renderForm()}
      </Dialog>
    </div>
  );
};

GetAppModal.propTypes = {
  closeModal: PropTypes.func,
  ctaLinks: PropTypes.shape({ QR_CODE: PropTypes.string }),
  // Overrides the ctaLinks QR, e.g. the header shows its own QR image
  qrImageSrc: PropTypes.string,
  // GTM "location" for the send-success event, one per entry point
  gtmLocation: PropTypes.string
};

GetAppModal.defaultProps = {
  closeModal: () => {},
  ctaLinks: DRUG_CLASS_CTA,
  qrImageSrc: '',
  gtmLocation: 'get app modal'
};

export default GetAppModal;
